import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { services } from "../mock";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = services[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!svc) return <Navigate to="/" replace />;

  const Icon = svc.icon;
  const others = Object.values(services).filter((s) => s.id !== svc.id);

  return (
    <div className="pt-[72px]">
      {/* HERO */}
      <section className="relative py-28 lg:py-36 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono-l text-sm text-stone-500">{svc.index}</span>
              <span className="h-px w-12 bg-[#c8a951]" />
              <Icon className="w-6 h-6 text-[#c8a951]" strokeWidth={1.5} />
              <span className="eyebrow">{svc.name}</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl lg:text-[5rem] font-bold tracking-tight leading-[1.02]">
              {svc.headline}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 text-stone-400 text-lg leading-relaxed max-w-2xl">{svc.intro}</p>
          </Reveal>
          <Reveal delay={240}>
            <Link to="/contact" className="btn-gold clip-corner mt-10 inline-flex px-7 py-3.5 text-sm font-semibold items-center gap-2">
              Request a consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-5">Capabilities</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-[1.05]">
              What we deliver.
            </h2>
          </Reveal>

          {svc.mediaCapabilities ? (
            <div className="mt-16 space-y-20">
              {svc.mediaCapabilities.map((c, i) => {
                const CIcon = c.icon;
                return (
                  <Reveal key={c.title} delay={i * 60}>
                    <div className="border-t border-white/15 pt-10">
                      <div className="flex items-start gap-5 mb-8 max-w-2xl">
                        <CIcon className="w-9 h-9 text-[#c8a951] shrink-0" strokeWidth={1.5} />
                        <div>
                          <h3 className="font-display text-2xl font-semibold mb-2">{c.title}</h3>
                          <p className="text-stone-400 leading-relaxed">{c.body}</p>
                        </div>
                      </div>
                      {c.single ? (
                        <figure className="relative overflow-hidden clip-corner border border-white/10 group max-w-3xl">
                          <img src={c.single} alt={c.title} loading="lazy" className="w-full h-[260px] lg:h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
                          {c.splitLabel && (
                            <>
                              <span className="absolute top-4 left-4 font-mono-l text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 clip-corner bg-black/70 text-stone-200 border border-white/15">
                                Before
                              </span>
                              <span className="absolute top-4 right-4 font-mono-l text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 clip-corner bg-[#c8a951] text-[#100f0a]">
                                After
                              </span>
                            </>
                          )}
                        </figure>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <BeforeAfter src={c.before} label="Before" tone="muted" alt={`${c.title} before`} />
                          <BeforeAfter src={c.after} label="After" tone="gold" alt={`${c.title} after`} />
                        </div>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          ) : (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {svc.capabilities.map((c, i) => {
                const CIcon = c.icon;
                return (
                  <Reveal key={c.title} delay={i * 80}>
                    <div className="bg-[#08080a] p-9 hover:bg-[#0d0d10] transition-colors h-full">
                      <CIcon className="w-8 h-8 text-[#c8a951] mb-6" strokeWidth={1.5} />
                      <h3 className="font-display text-xl font-semibold mb-3">{c.title}</h3>
                      <p className="text-stone-400 text-sm leading-relaxed">{c.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 border-t border-white/10 relative">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-5">How we engage</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-[1.05]">
              A disciplined process.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {svc.process.map((p, i) => (
              <Reveal key={p.step} delay={i * 90}>
                <div className="border-t border-white/15 pt-6 h-full">
                  <span className="font-display text-4xl font-bold gold-text">{p.step}</span>
                  <h3 className="font-display text-lg font-semibold mt-4 mb-2">{p.title}</h3>
                  <p className="text-stone-400 text-sm leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="eyebrow mb-8">Explore more</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map((o) => {
              const OIcon = o.icon;
              return (
                <Link key={o.id} to={`/services/${o.id}`} className="svc-card border border-white/10 clip-corner p-8 flex items-center justify-between group">
                  <div className="flex items-center gap-5">
                    <OIcon className="w-8 h-8 text-[#c8a951]" strokeWidth={1.5} />
                    <div>
                      <span className="font-mono-l text-xs text-stone-500">{o.index}</span>
                      <h3 className="font-display text-xl font-semibold group-hover:text-[#c8a951] transition-colors">{o.name}</h3>
                    </div>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-stone-600 group-hover:text-[#c8a951] transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function BeforeAfter({ src, label, tone, alt }) {
  return (
    <figure className="relative overflow-hidden clip-corner border border-white/10 group">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-[240px] lg:h-[300px] object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <figcaption
        className={`absolute top-4 left-4 font-mono-l text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 clip-corner ${
          tone === "gold"
            ? "bg-[#c8a951] text-[#100f0a]"
            : "bg-black/70 text-stone-200 border border-white/15"
        }`}
      >
        {label}
      </figcaption>
    </figure>
  );
}
